'use client';
import React, { useState } from 'react';
import Link from 'next/link';

interface ServiceItem {
  title: string;
  desc: string;
  href: string;
  icon: React.ReactNode;
  badge?: string;
}

interface ServiceCategoryProps {
  id: string;
  categoryTitle: string;
  categoryIcon: React.ReactNode;
  color: string;
  services: ServiceItem[];
}

export default function ServiceCategorySection({
  id,
  categoryTitle,
  categoryIcon,
  color,
  services,
}: ServiceCategoryProps) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? services : services.slice(0, 4);

  return (
    <div id={id} className="mb-12" dir="rtl">
      {/* Category Header */}
      <div className={`flex items-center gap-3 mb-6 pb-3 border-b-2 ${color}`}>
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          {categoryIcon}
        </div>
        <h3 className="text-xl font-bold text-foreground">{categoryTitle}</h3>
        <span className="mr-auto text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
          {services.length} خدمة
        </span>
      </div>

      {/* Services Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4">
        {visible.map((service) => (
          <Link key={service.title} href={service.href}>
            <div className="service-card group flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center shrink-0 group-hover:bg-accent/20 transition-colors">
                {service.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <h4 className="font-semibold text-sm text-foreground leading-snug group-hover:text-accent transition-colors">
                    {service.title}
                  </h4>
                  {service.badge && (
                    <span className="badge-new shrink-0">{service.badge}</span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{service.desc}</p>
              </div>
              <svg
                className="w-4 h-4 text-muted-foreground group-hover:text-accent transition-colors shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </div>
          </Link>
        ))}
      </div>

      {services.length > 4 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-4 text-sm text-accent hover:text-accent-dark font-medium flex items-center gap-1 transition-colors"
        >
          {expanded ? 'عرض أقل' : `عرض الكل (${services.length - 4} أخرى)`}
          <svg
            className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      )}
    </div>
  );
}
